import { ReactNode } from 'react';
import { Link as RouterLink, useLocation } from 'react-router';
import AppBar from '@mui/material/AppBar';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import { Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TextV2 } from '@insights-ltd/design-library/components';
import { useAuth } from 'contexts/AuthContext';
import LeadingColourEnergyIndicator from 'components/LeadingColourEnergyIndicator/LeadingColourEnergyIndicator';
import useIsAboveMobile from 'components/hooks/useIsAboveMobile';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import InsightsLogo from 'components/Svgs/ASSET-InsightsLogoBlue.svg';

type HeaderLayoutProps = {
  children: ReactNode;
};

const UserInfo = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const { pathname } = useLocation();

  return (
    <Grid
      container
      sx={[
        {
          alignItems: 'center',
        },
        (theme) => ({
          height: '100%',
          padding: `0 ${theme.spacing(spacingSizeMap.M)}`,
          borderRight: `1px solid ${theme.palette.grey[400]}`,
          borderLeft: `1px solid ${theme.palette.grey[400]}`,
        }),
      ]}
    >
      <Grid>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <LeadingColourEnergyIndicator
            leadingColourEnergy={
              user?.leadingColourEnergy === null
                ? undefined
                : user?.leadingColourEnergy
            }
          />
          <TextV2
            component="p"
            data-testid="header-user-name"
            variant="bodyBold"
          >
            {user?.fullName}
          </TextV2>
        </Box>
        <TextV2 component="p" variant="bodyBold">
          <Link
            component={RouterLink}
            to="/account"
            sx={(theme) => ({
              color:
                pathname === '/account'
                  ? theme.palette.grey[700]
                  : theme.palette.blue.main,
            })}
          >
            {t('ui.event-management.my-account.title')}
          </Link>
        </TextV2>
      </Grid>
    </Grid>
  );
};

const HeaderLayout = ({ children }: HeaderLayoutProps) => {
  const aboveMobile = useIsAboveMobile('md');
  const { t } = useTranslation();

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={(theme) => ({
        backgroundColor: theme.palette.grey[100],
        borderBottom: `1px solid ${theme.palette.grey[400]}`,
      })}
    >
      <Container maxWidth="lg" translate="no">
        <Grid container direction="row" wrap="nowrap">
          <Grid
            container
            sx={[
              {
                alignItems: 'center',
              },
              (theme) => ({
                width: '100%',
                paddingTop: theme.spacing(spacingSizeMap.M),
              }),
            ]}
          >
            <Link component={RouterLink} to="/dashboard" data-testid="logo-svg">
              <Box
                sx={(theme) => ({
                  marginBottom: theme.spacing(spacingSizeMap.S),
                })}
              >
                <img
                  alt={t('ui.io.profile.discovery.footer.logo_title')}
                  src={InsightsLogo}
                  width="128px"
                  height="34px"
                />
              </Box>
            </Link>
            <Grid
              size="grow"
              sx={{
                height: '100%',
              }}
            >
              {children}
            </Grid>
          </Grid>
          {aboveMobile && (
            <Grid size={{ xs: 6, sm: 6, md: 3 }}>
              <UserInfo />
            </Grid>
          )}
        </Grid>
      </Container>
    </AppBar>
  );
};

export default HeaderLayout;
